// Shared reduction for Soundcharts audience API responses. Each date's `plots` array has one
// entry per platform ID linked to the song, and what those entries mean depends on the
// platform:
//
// - Spotify repeats the *same* cumulative count under every linked track ID (single, album,
//   deluxe...). Summing them multiplies the total by the number of IDs, so we take one
//   distinct value per date instead.
// - YouTube, SoundCloud and Audiomack give each ID its own count (on YouTube every ID is a
//   separate video: music video, lyric video, visualizer, official audio). The song's total
//   is the sum across IDs. IDs come and go from day to day, so each ID contributes its own
//   last value on or before the end date rather than whatever happened to report that day.

export interface AudiencePlot {
  identifier?: string;
  value: number;
}

export interface AudienceItem {
  date: string;
  plots: AudiencePlot[];
}

export interface AudiencePoint {
  date: string;
  value: number;
}

// Platforms whose IDs each carry their own count and are added together.
const SUMMED_PLATFORMS = new Set(["youtube", "soundcloud", "audiomack"]);

export const FIRST_MONTH_DAYS = 30;

// Soundcharts returns full ISO datetimes (e.g. "2026-03-08T00:00:00+00:00") while callers
// compare against a plain "yyyy-MM-dd" date. Keep only the date part so string comparison
// is apples-to-apples -- otherwise the datetime always sorts "after" the shorter target date.
const toPlainDate = (date: string) => date.slice(0, 10);

const usableItems = (items: AudienceItem[] | undefined | null) =>
  (items || []).filter((item) => item?.date && Array.isArray(item.plots) && item.plots.length > 0);

// Spotify: collapses each date's `plots` down to a single distinct value, then walks the
// series oldest-to-newest so the cumulative total never dips below a prior date. When IDs
// disagree the smallest is used: in the disagreements observed, the higher ID was reporting
// the *next* day's number.
export function reduceAudienceSeries(items: AudienceItem[] | undefined | null): AudiencePoint[] {
  const perDate = usableItems(items)
    .map((item) => {
      const distinct = Array.from(new Set(item.plots.map((p) => p.value)));
      return { date: toPlainDate(item.date), value: Math.min(...distinct) };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  let previous = 0;
  for (const point of perDate) {
    if (point.value < previous) {
      point.value = previous;
    }
    previous = point.value;
  }

  return perDate;
}

// Soundcharts doesn't return a row for every day. To read the value "as of" a target date,
// take the last row on or before it -- never a later row, and never a fixed array position.
export function getAudienceValueOnOrBefore(
  series: AudiencePoint[],
  targetDate: string
): number | null {
  let result: number | null = null;
  for (const point of series) {
    if (point.date > targetDate) break;
    result = point.value;
  }
  return result;
}

// YouTube / SoundCloud / Audiomack: every linked ID counts, with no filtering. Each ID
// contributes its own last value on or before the end date, so a video missing from the
// end date's row still counts with its last known value, and a video that first appears
// partway through the month counts in full. Returns null when no ID has any row on or
// before the end date.
export function sumIdentifiersOnOrBefore(
  items: AudienceItem[] | undefined | null,
  endDate: string
): number | null {
  const latestById = new Map<string, { date: string; value: number }>();

  for (const item of usableItems(items)) {
    const date = toPlainDate(item.date);
    if (date > endDate) continue;
    item.plots.forEach((plot, index) => {
      // An entry without an identifier can't be matched across dates; keep it as its own ID.
      const id = plot.identifier ?? `unidentified-${date}-${index}`;
      const latest = latestById.get(id);
      if (!latest || date > latest.date) {
        latestById.set(id, { date, value: plot.value });
      }
    });
  }

  if (latestById.size === 0) return null;
  let total = 0;
  latestById.forEach(({ value }) => {
    total += value;
  });
  return total;
}

// The first-month number shown for one platform: the value as of `endDate`, or null
// ("No data") when nothing was reported on or before it.
export function getFirstMonthAudience(
  items: AudienceItem[] | undefined | null,
  platform: string,
  endDate: string
): number | null {
  if (SUMMED_PLATFORMS.has(platform)) {
    return sumIdentifiersOnOrBefore(items, endDate);
  }
  return getAudienceValueOnOrBefore(reduceAudienceSeries(items), endDate);
}

// First month = release date through release date + 30 days. Soundcharts' release date wins
// because it is the real one; the date entered in Cadence is only a fallback, since a pitch
// is usually created before the song is out and Soundcharts has no date for it yet.
export function getFirstMonthWindow(
  soundchartsReleaseDate: string | null | undefined,
  cadenceReleaseDate: string | null | undefined
): { startDate: string; endDate: string } | null {
  const releaseDate = soundchartsReleaseDate || cadenceReleaseDate;
  if (!releaseDate) return null;

  const start = new Date(`${toPlainDate(releaseDate)}T00:00:00Z`);
  if (isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + FIRST_MONTH_DAYS);

  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}
